// Polyfills for older browser compatibility
// These polyfills ensure critical APIs work in older browsers (iOS Safari < 10.3, Android 4.4, etc.)

/**
 * URLSearchParams Polyfill
 * Provides URLSearchParams API for browsers that don't support it (iOS Safari < 10.3, Android 4.4)
 * This is critical for Vipps callback parsing in home.jsx
 */
function polyfillURLSearchParams() {
    if (typeof URLSearchParams !== 'undefined') {
        // Already supported, no polyfill needed
        return;
    }

    // URLSearchParams polyfill implementation
    function URLSearchParamsPolyfill(search) {
        this._params = {};
        
        if (typeof search === 'string') {
            // Remove leading '?' if present
            if (search.charAt(0) === '?') {
                search = search.substring(1);
            }
            
            // Parse query string
            var pairs = search.split('&');
            for (var i = 0; i < pairs.length; i++) {
                var pair = pairs[i];
                if (!pair) continue;
                
                var equalIndex = pair.indexOf('=');
                if (equalIndex === -1) {
                    // No value, just key
                    var key = decodeURIComponent(pair);
                    this._params[key] = this._params[key] ? [this._params[key], ''].flat() : '';
                } else {
                    var key = decodeURIComponent(pair.substring(0, equalIndex));
                    var value = decodeURIComponent(pair.substring(equalIndex + 1));
                    
                    if (this._params[key]) {
                        // Multiple values for same key
                        if (Array.isArray(this._params[key])) {
                            this._params[key].push(value);
                        } else {
                            this._params[key] = [this._params[key], value];
                        }
                    } else {
                        this._params[key] = value;
                    }
                }
            }
        } else if (search && typeof search === 'object') {
            // Handle object or existing URLSearchParams
            if (search instanceof URLSearchParamsPolyfill) {
                this._params = JSON.parse(JSON.stringify(search._params));
            } else {
                // Object with key-value pairs
                for (var key in search) {
                    if (search.hasOwnProperty(key)) {
                        this._params[key] = search[key];
                    }
                }
            }
        }
    }

    URLSearchParamsPolyfill.prototype.get = function(name) {
        var value = this._params[name];
        if (Array.isArray(value)) {
            return value[0] || null;
        }
        return value !== undefined ? value : null;
    };

    URLSearchParamsPolyfill.prototype.getAll = function(name) {
        var value = this._params[name];
        if (Array.isArray(value)) {
            return value;
        }
        return value !== undefined ? [value] : [];
    };

    URLSearchParamsPolyfill.prototype.set = function(name, value) {
        this._params[name] = value;
    };

    URLSearchParamsPolyfill.prototype.append = function(name, value) {
        if (this._params[name]) {
            if (Array.isArray(this._params[name])) {
                this._params[name].push(value);
            } else {
                this._params[name] = [this._params[name], value];
            }
        } else {
            this._params[name] = value;
        }
    };

    URLSearchParamsPolyfill.prototype.delete = function(name) {
        delete this._params[name];
    };

    URLSearchParamsPolyfill.prototype.has = function(name) {
        return name in this._params;
    };

    URLSearchParamsPolyfill.prototype.toString = function() {
        var pairs = [];
        for (var key in this._params) {
            if (this._params.hasOwnProperty(key)) {
                var value = this._params[key];
                if (Array.isArray(value)) {
                    for (var i = 0; i < value.length; i++) {
                        pairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(value[i]));
                    }
                } else {
                    pairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
                }
            }
        }
        return pairs.join('&');
    };

    // Make it available globally
    window.URLSearchParams = URLSearchParamsPolyfill;
    
    console.info('URLSearchParams polyfill loaded');
}

/**
 * Promise Polyfill (minimal implementation)
 * Provides basic Promise support for very old browsers (Android 4.4 and older)
 * Note: This is a minimal polyfill - full Promise polyfill is recommended for production
 */
function polyfillPromise() {
    if (typeof Promise !== 'undefined') {
        // Already supported, no polyfill needed
        return;
    }

    // Minimal Promise polyfill - only implements what we need
    // For production, consider using a full polyfill library like es6-promise
    console.warn('Promise is not supported in this browser. App functionality may be limited.');
    
    // Basic Promise implementation
    function PromisePolyfill(executor) {
        var self = this;
        self.state = 'pending';
        self.value = undefined;
        self.handlers = [];

        function resolve(result) {
            if (self.state === 'pending') {
                self.state = 'fulfilled';
                self.value = result;
                self.handlers.forEach(handle);
                self.handlers = null;
            }
        }

        function reject(error) {
            if (self.state === 'pending') {
                self.state = 'rejected';
                self.value = error;
                self.handlers.forEach(handle);
                self.handlers = null;
            }
        }

        function handle(handler) {
            if (self.state === 'pending') {
                self.handlers.push(handler);
            } else {
                if (self.state === 'fulfilled' && typeof handler.onFulfilled === 'function') {
                    handler.onFulfilled(self.value);
                }
                if (self.state === 'rejected' && typeof handler.onRejected === 'function') {
                    handler.onRejected(self.value);
                }
            }
        }

        self.then = function(onFulfilled, onRejected) {
            return new PromisePolyfill(function(resolve, reject) {
                handle({
                    onFulfilled: function(result) {
                        try {
                            resolve(onFulfilled ? onFulfilled(result) : result);
                        } catch (ex) {
                            reject(ex);
                        }
                    },
                    onRejected: function(error) {
                        try {
                            resolve(onRejected ? onRejected(error) : error);
                        } catch (ex) {
                            reject(ex);
                        }
                    }
                });
            });
        };

        self.catch = function(onRejected) {
            return self.then(null, onRejected);
        };

        try {
            executor(resolve, reject);
        } catch (ex) {
            reject(ex);
        }
    }

    PromisePolyfill.resolve = function(value) {
        return new PromisePolyfill(function(resolve) {
            resolve(value);
        });
    };

    PromisePolyfill.reject = function(reason) {
        return new PromisePolyfill(function(resolve, reject) {
            reject(reason);
        });
    };

    PromisePolyfill.race = function(promises) {
        return new PromisePolyfill(function(resolve, reject) {
            for (var i = 0; i < promises.length; i++) {
                promises[i].then(resolve, reject);
            }
        });
    };

    // Make it available globally
    window.Promise = PromisePolyfill;
    
    console.info('Promise polyfill loaded (minimal implementation)');
}

/**
 * Initialize all polyfills
 * Should be called before React app starts (in index.js)
 */
export function initializePolyfills() {
    try {
        polyfillURLSearchParams();
        polyfillPromise();
        console.debug('Polyfills initialized');
    } catch (error) {
        console.error('Error initializing polyfills:', error);
    }
}

